import { ButtonSearch } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { useStore } from '@/hooks/useStore';
import { ROLE_USER } from '@/types/enums';
import { monthsCombobox, yearsCombobox } from '@/utils/datetime';
import { cn } from '@/utils/utils';
import { observer } from 'mobx-react-lite';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface FilterFormProps {
    form: UseFormReturn<any>,
    isOwner: boolean,
    isInModal?: boolean,
    onSearch: (searchParams?: any) => void
}

export default observer(function FilterForm({ form, isOwner, isInModal = false, onSearch }: FilterFormProps) {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        accountStore: { accounts },
        locationStore: { locations },
        bikeStore: { bikeModelListIncluesDeleted }
    } = useStore();

    const years = yearsCombobox();
    const months = monthsCombobox;

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSearch)}>
                <div className={cn(
                    'grid gap-4 md:gap-6',
                    !isOwner ? 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
                    isInModal && 'grid-cols-1 sm:grid-cols-2'
                )}
                >
                    {!isOwner && (
                        <FormField
                            control={form.control}
                            name='ownerId'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.owner')}
                                            value={field.value ?? ''}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            items={
                                                (Array.isArray(accounts) && accounts?.length ?
                                                    [
                                                        ...accounts
                                                            .filter(account => (
                                                                Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_EK &&
                                                                Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_RD
                                                            ))
                                                            .map(item => ({
                                                                label: item.fullname,
                                                                labelSearch: item.fullname + item?.userCode,
                                                                value: item.id.toString(),
                                                                code: item.id
                                                            }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    )}
                    <FormField
                        control={form.control}
                        name='locationId'
                        render={({ field }) => (
                            <FormItem className={cn(isInModal && isOwner && 'col-span-full')}>
                                <FormControl>
                                    <Combobox
                                        placeholder={t('field.location_name')}
                                        value={field.value ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value ?? '');
                                        }}
                                        items={
                                            (Array.isArray(locations) && locations?.length ?
                                                [
                                                    ...locations
                                                        .filter(item => item.name)
                                                        .map(item => ({
                                                            label: item.name,
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                ] :
                                                []
                                            )
                                        }
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    {!isInModal && (
                        <FormField
                            control={form.control}
                            name='bikeModel'
                            render={({ field }) => (
                                <FormItem className='w-full'>
                                    <FormControl>
                                        <Combobox
                                            placeholder={t('field.model_name')}
                                            value={field.value}
                                            onChange={(item) => {
                                                field.onChange(item?.value ?? '');
                                            }}
                                            search={true}
                                            items={
                                                (Array.isArray(bikeModelListIncluesDeleted) && bikeModelListIncluesDeleted?.length > 0 ?
                                                    [
                                                        ...bikeModelListIncluesDeleted.map(item => ({
                                                            label: item.bikeModel,
                                                            value: item.bikeModel,
                                                            code: item.bikeModel
                                                        }))
                                                    ] :
                                                    []
                                                )
                                            }
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    )}

                    {isInModal ?
                        (
                            <div className='col-span-full grid grid-cols-2 gap-4 md:gap-6'>
                                <FormField
                                    control={form.control}
                                    name='year'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Combobox
                                                    placeholder={t('field.year')}
                                                    value={field.value.toString() ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value ?? '');
                                                    }}
                                                    search={false}
                                                    items={years.map(year => ({
                                                        label: `${year}年`,
                                                        value: year.toString()
                                                    }))}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='month'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Combobox
                                                    placeholder={t('field.month')}
                                                    value={field.value?.toString() ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value ?? '');
                                                    }}
                                                    search={false}
                                                    items={months.map(month => ({
                                                        label: `${month}月`,
                                                        value: month.toString()
                                                    }))}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>
                        ) :
                        (
                            <>
                                <FormField
                                    control={form.control}
                                    name='year'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Combobox
                                                    placeholder={t('field.year')}
                                                    value={field.value.toString() ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value ?? '');
                                                    }}
                                                    search={false}
                                                    items={years.map(year => ({
                                                        label: `${year}年`,
                                                        value: year.toString()
                                                    }))}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name='month'
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Combobox
                                                    placeholder={t('field.month')}
                                                    value={field.value?.toString() ?? ''}
                                                    onChange={(item) => {
                                                        field.onChange(item?.value ?? '');
                                                    }}
                                                    search={false}
                                                    items={months.map(month => ({
                                                        label: `${month}月`,
                                                        value: month.toString()
                                                    }))}
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </>
                        )}

                    {!isOwner && (
                        <FormField
                            control={form.control}
                            name='keyword'
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <Input
                                            type='text'
                                            placeholder={t('field.reservation_code_renter_name')}
                                            {...field}
                                        />
                                    </FormControl>
                                </FormItem>
                            )}
                        />
                    )}

                    <div className='col-span-full flex justify-center'>
                        <ButtonSearch size='md' className='max-w-52'>
                            {t('button.search')}
                        </ButtonSearch>
                    </div>

                </div>
            </form>
        </Form>
    );
});
